import { useMemo } from "react";
import { Box, Stack, Checkbox, Typography, FormControlLabel } from "@material-ui/core";
import { groupPropertyTypes } from "utils/listings/propertyTypeGroups";

// ──────────────────────────────────────────────────────────────────────────────
// "סוג נכס" picker for surfaces where a Select is the WRONG control — a narrow
// popover or a filter menu that is itself already a dropdown (nesting a Select's
// portalled menu inside one fights the outer click-away and clips badly).
//
// Same taxonomy, same section titles and same `string[]` value shape as
// `PropertyTypeSelect`; only the presentation differs. Both read
// `groupPropertyTypes()` so the two can never disagree about which section a
// type belongs to, and a newly added enum member shows up in both at once.
//
// Section headers double as select-all/none for their group: with 30+ types in
// a 300px popover, "all the commercial ones" was otherwise ~12 taps.
// ──────────────────────────────────────────────────────────────────────────────

export type PropertyTypeCheckboxListProps = {
  /** Available options — the shared enum, or Algolia facet values. */
  options: readonly string[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  /** Scroll cap for the list. The header row is rendered by the caller. */
  maxHeight?: number | string;
};

export function PropertyTypeCheckboxList({
  options,
  value,
  onChange,
  disabled,
  maxHeight = 260
}: PropertyTypeCheckboxListProps): JSX.Element {
  const groups = useMemo(() => groupPropertyTypes(options), [options]);
  const selected = value ?? [];

  const toggle = (option: string) => (): void => {
    onChange(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected, option]
    );
  };

  // Preserve the caller's option order on select-all rather than appending the
  // group verbatim, so the emitted array stays comparable across interactions.
  const toggleGroup = (groupOptions: string[]) => (): void => {
    const allOn = groupOptions.every((option) => selected.includes(option));
    onChange(
      allOn
        ? selected.filter((item) => !groupOptions.includes(item))
        : [...selected, ...groupOptions.filter((option) => !selected.includes(option))]
    );
  };

  return (
    <Stack spacing={0.5} sx={{ maxHeight, overflowY: "auto" }}>
      {groups.map((group) => {
        const allOn = group.options.every((option) => selected.includes(option));
        const someOn = !allOn && group.options.some((option) => selected.includes(option));

        return (
          <Box key={group.id}>
            {/* A button, not a bare label: MUI's Checkbox owns the a11y state
                (checked / indeterminate) and the FormControlLabel makes the
                whole title row the hit target. */}
            <FormControlLabel
              disabled={disabled}
              sx={{
                mx: 0,
                width: "100%",
                "& .MuiTypography-root": {
                  typography: "caption",
                  fontWeight: 700,
                  color: "text.secondary"
                }
              }}
              control={
                <Checkbox
                  size="small"
                  checked={allOn}
                  indeterminate={someOn}
                  onChange={toggleGroup(group.options)}
                />
              }
              label={group.label}
            />
            <Stack spacing={0} sx={{ pl: 2 }}>
              {group.options.map((option) => (
                <FormControlLabel
                  key={option}
                  disabled={disabled}
                  sx={{ mx: 0, "& .MuiTypography-root": { typography: "body2" } }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(option)}
                      onChange={toggle(option)}
                    />
                  }
                  label={option}
                />
              ))}
            </Stack>
          </Box>
        );
      })}
      {groups.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
          אין סוגי נכס זמינים
        </Typography>
      )}
    </Stack>
  );
}

export default PropertyTypeCheckboxList;
